import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence, useScroll, useTransform } from 'framer-motion';
import { 
    ArrowRight, Shield, Zap, TrendingUp, TrendingDown, Bot, Lock, Globe, 
    ChevronDown, Star, Users, Award, BarChart3, Wallet,
    CheckCircle, Menu, X, Sparkles, Clock, PieChart, 
    LineChart, Target, Cpu, Phone, MapPin, Mail, Play,
    ChevronRight, ArrowUpRight, Layers, Eye, RefreshCw,
    Bitcoin, Activity, Hexagon, ExternalLink, Quote, Copy, Key,
    Rocket, Crown, Gift, Percent, ArrowDown, Check, Minus
} from 'lucide-react';
import LandingChat from '../components/LandingChat';

const LOGO_URL = "/logo.jpg";

// Testimonial images
const TESTIMONIAL_IMAGES = {
    michael: "https://static.prod-images.emergentagent.com/jobs/c9e44c33-3a00-4794-90e4-818960de0423/images/e752334a1a38fb627047a46ef66095d329c1d125574463d20551cd79f0ec4531.png",
    elena: "https://static.prod-images.emergentagent.com/jobs/c9e44c33-3a00-4794-90e4-818960de0423/images/8733cc4701b3de85864fc4945dfcbba101e802032d7a7171d71b0e7e64e6d3b2.png",
    david: "https://static.prod-images.emergentagent.com/jobs/c9e44c33-3a00-4794-90e4-818960de0423/images/f947ae378a117a9bd56f95060b35d51eb7e85396e4d9aee3974c309d8f1f9447.png"
};

// ============================================
// UNIQUE FEATURE 1: Live Crypto Price Ticker
// ============================================
const LivePriceTicker = () => {
    const [prices, setPrices] = useState([]);

    const fetchPrices = async () => {
        try {
            const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/crypto/top-coins?limit=10`);
            if (!res.ok) return;
            const data = await res.json();
            const coins = (data.coins || []).map(c => ({
                symbol: c.symbol,
                price: c.current_price || 0,
                change: c.price_change_24h || 0,
            }));
            if (coins.length) setPrices(coins);
        } catch (e) {
            // silent fallback
        }
    };

    useEffect(() => {
        fetchPrices();
        const interval = setInterval(fetchPrices, 30000); // refresh every 30s
        return () => clearInterval(interval);
    }, []);

    if (prices.length === 0) {
        return (
            <div className="relative overflow-hidden bg-black/40 backdrop-blur-sm border-y border-[#5BA4A4]/20 h-10 flex items-center px-6">
                <span className="text-xs text-gray-500">Loading live market prices…</span>
            </div>
        );
    }

    return (
        <div className="relative overflow-hidden bg-black/40 backdrop-blur-sm border-y border-[#5BA4A4]/20">
            <div className="flex animate-scroll">
                {[...prices, ...prices].map((coin, i) => (
                    <div key={i} className="flex items-center gap-4 px-6 py-2 whitespace-nowrap">
                        <span className="text-white font-bold">{coin.symbol}</span>
                        <span className="text-white font-mono">${coin.price.toLocaleString(undefined, { minimumFractionDigits: coin.price < 1 ? 4 : 2, maximumFractionDigits: coin.price < 1 ? 6 : 2 })}</span>
                        <span className={`text-sm font-mono ${coin.change >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                            {coin.change >= 0 ? '+' : ''}{coin.change.toFixed(2)}%
                        </span>
                    </div>
                ))}
            </div>
            <style>{`
                @keyframes scroll {
                    0% { transform: translateX(0); }
                    100% { transform: translateX(-50%); }
                }
                .animate-scroll {
                    animation: scroll 30s linear infinite;
                }
            `}</style>
        </div>
    );
};

// ============================================
// UNIQUE FEATURE 2: Interactive Globe
// ============================================
const InteractiveGlobe = () => {
    const [trades, setTrades] = useState([]);
    
    useEffect(() => {
        const cities = [
            { name: 'New York', x: 25, y: 35 },
            { name: 'London', x: 48, y: 30 },
            { name: 'Singapore', x: 78, y: 55 },
            { name: 'Tokyo', x: 85, y: 38 },
            { name: 'Sydney', x: 88, y: 75 },
            { name: 'Dubai', x: 60, y: 45 },
            { name: 'Frankfurt', x: 50, y: 32 },
            { name: 'Hong Kong', x: 80, y: 48 },
            { name: 'São Paulo', x: 30, y: 70 },
            { name: 'Mumbai', x: 68, y: 50 }
        ];
        
        const interval = setInterval(() => {
            const city = cities[Math.floor(Math.random() * cities.length)];
            const newTrade = {
                id: Date.now(),
                ...city,
                type: Math.random() > 0.5 ? 'buy' : 'sell',
                amount: (Math.random() * 50000 + 1000).toFixed(0)
            };
            setTrades(prev => [...prev.slice(-8), newTrade]);
        }, 1500);
        
        return () => clearInterval(interval);
    }, []);

    return (
        <div className="relative w-full aspect-[2/1] max-w-4xl mx-auto">
            {/* Globe SVG */}
            <svg viewBox="0 0 100 50" className="w-full h-full opacity-30">
                {/* Grid lines */}
                {[...Array(9)].map((_, i) => (
                    <path
                        key={`h-${i}`}
                        d={`M 0 ${5 + i * 5} Q 50 ${i % 2 === 0 ? 5 + i * 5 - 3 : 5 + i * 5 + 3} 100 ${5 + i * 5}`}
                        fill="none"
                        stroke="#5BA4A4"
                        strokeWidth="0.2"
                        strokeDasharray="1,1"
                    />
                ))}
                {[...Array(19)].map((_, i) => (
                    <line
                        key={`v-${i}`}
                        x1={5 + i * 5}
                        y1="0"
                        x2={5 + i * 5}
                        y2="50"
                        stroke="#5BA4A4"
                        strokeWidth="0.2"
                        strokeDasharray="1,1"
                    />
                ))}
            </svg>
            
            {/* Trade dots */}
            <AnimatePresence>
                {trades.map(trade => (
                    <motion.div
                        key={trade.id}
                        className="absolute"
                        style={{ left: `${trade.x}%`, top: `${trade.y}%` }}
                        initial={{ scale: 0, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        exit={{ scale: 0, opacity: 0 }}
                    >
                        <motion.div
                            className={`w-3 h-3 rounded-full ${trade.type === 'buy' ? 'bg-green-500' : 'bg-red-500'}`}
                            animate={{ scale: [1, 2, 1], opacity: [1, 0.5, 1] }}
                            transition={{ duration: 2, repeat: Infinity }}
                        />
                        <motion.div
                            className={`absolute inset-0 rounded-full ${trade.type === 'buy' ? 'bg-green-500' : 'bg-red-500'}`}
                            initial={{ scale: 1, opacity: 0.5 }}
                            animate={{ scale: 4, opacity: 0 }}
                            transition={{ duration: 1.5, repeat: Infinity }}
                        />
                        <motion.div
                            className="absolute left-4 top-0 whitespace-nowrap"
                            initial={{ opacity: 0, x: -10 }}
                            animate={{ opacity: 1, x: 0 }}
                        >
                            <span className={`text-[10px] font-bold ${trade.type === 'buy' ? 'text-green-400' : 'text-red-400'}`}>
                                ${Number(trade.amount).toLocaleString()}
                            </span>
                        </motion.div>
                    </motion.div>
                ))}
            </AnimatePresence>
            
            {/* Title */}
            <div className="absolute bottom-0 left-0 right-0 text-center">
                <p className="text-xs text-gray-500">Live trades from 190+ countries</p>
            </div>
        </div>
    );
};

// ============================================
// UNIQUE FEATURE 3: Particle Constellation
// ============================================
const ParticleConstellation = () => {
    const canvasRef = useRef(null);
    
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        
        const ctx = canvas.getContext('2d');
        const particles = [];
        const particleCount = 80;
        
        const resize = () => {
            canvas.width = window.innerWidth;
            canvas.height = window.innerHeight;
        };
        resize();
        window.addEventListener('resize', resize);
        
        // Create particles
        for (let i = 0; i < particleCount; i++) {
            particles.push({
                x: Math.random() * canvas.width,
                y: Math.random() * canvas.height,
                vx: (Math.random() - 0.5) * 0.5,
                vy: (Math.random() - 0.5) * 0.5,
                radius: Math.random() * 2 + 1
            });
        }
        
        const animate = () => {
            ctx.clearRect(0, 0, canvas.width, canvas.height);
            
            // Update and draw particles
            particles.forEach((p, i) => {
                p.x += p.vx;
                p.y += p.vy;
                
                if (p.x < 0 || p.x > canvas.width) p.vx *= -1;
                if (p.y < 0 || p.y > canvas.height) p.vy *= -1;
                
                // Draw particle
                ctx.beginPath();
                ctx.arc(p.x, p.y, p.radius, 0, Math.PI * 2);
                ctx.fillStyle = 'rgba(91, 164, 164, 0.6)';
                ctx.fill();
                
                // Draw connections
                particles.forEach((p2, j) => {
                    if (i === j) return;
                    const dist = Math.hypot(p.x - p2.x, p.y - p2.y);
                    if (dist < 150) {
                        ctx.beginPath();
                        ctx.moveTo(p.x, p.y);
                        ctx.lineTo(p2.x, p2.y);
                        ctx.strokeStyle = `rgba(91, 164, 164, ${0.2 * (1 - dist / 150)})`;
                        ctx.stroke();
                    }
                });
            });
            
            requestAnimationFrame(animate);
        };
        animate();
        
        return () => window.removeEventListener('resize', resize);
    }, []);
    
    return <canvas ref={canvasRef} className="fixed inset-0 pointer-events-none z-0" />;
};

// ============================================
// UNIQUE FEATURE 4: How It Works Section
// ============================================
const HowItWorks = () => {
    const steps = [
        {
            icon: Rocket,
            title: "Create Account",
            description: "Sign up in 60 seconds with just your email",
            color: "#5BA4A4"
        },
        {
            icon: Key,
            title: "Get Your Wallet",
            description: "Receive your personal wallet with private keys",
            color: "#8B5CF6"
        },
        {
            icon: Wallet,
            title: "Fund & Trade",
            description: "Deposit crypto and start trading instantly",
            color: "#22C55E"
        },
        {
            icon: Bot,
            title: "Activate AI",
            description: "Let CyberAI trade 24/7 while you sleep",
            color: "#F59E0B"
        }
    ];

    return (
        <section className="relative py-16 md:py-24 z-10">
            <div className="max-w-7xl mx-auto px-4 md:px-8">
                <motion.div 
                    className="text-center mb-12 md:mb-16"
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                >
                    <span className="text-[#5BA4A4] text-xs font-semibold uppercase tracking-[0.2em]">Simple Process</span>
                    <h2 className="text-2xl md:text-4xl lg:text-5xl font-black mt-3 mb-4">
                        Start Trading in{' '}
                        <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#5BA4A4] to-purple-400">4 Easy Steps</span>
                    </h2>
                </motion.div>

                <div className="relative">
                    {/* Connection line */}
                    <div className="hidden lg:block absolute top-1/2 left-0 right-0 h-1 bg-gradient-to-r from-[#5BA4A4] via-purple-500 to-[#22C55E] transform -translate-y-1/2 rounded-full" />
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
                        {steps.map((step, i) => (
                            <motion.div
                                key={i}
                                className="relative"
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: i * 0.15 }}
                            >
                                <div className="relative p-6 bg-gradient-to-br from-[#0a0a0a] to-[#111] border border-white/10 rounded-2xl hover:border-[#5BA4A4]/50 transition-all group">
                                    {/* Step number */}
                                    <div 
                                        className="absolute -top-3 -left-3 w-8 h-8 rounded-full flex items-center justify-center text-sm font-black text-black"
                                        style={{ backgroundColor: step.color }}
                                    >
                                        {i + 1}
                                    </div>
                                    
                                    {/* Icon */}
                                    <motion.div
                                        className="w-16 h-16 rounded-2xl flex items-center justify-center mb-4 mx-auto"
                                        style={{ backgroundColor: `${step.color}20`, border: `2px solid ${step.color}40` }}
                                        whileHover={{ rotate: [0, -10, 10, 0], scale: 1.1 }}
                                        transition={{ duration: 0.5 }}
                                    >
                                        <step.icon size={28} style={{ color: step.color }} />
                                    </motion.div>
                                    
                                    <h3 className="text-lg font-bold text-center mb-2">{step.title}</h3>
                                    <p className="text-sm text-gray-400 text-center">{step.description}</p>
                                </div>
                                
                                {/* Arrow */}
                                {i < 3 && (
                                    <div className="hidden lg:flex absolute top-1/2 -right-4 transform -translate-y-1/2 z-10">
                                        <motion.div
                                            animate={{ x: [0, 5, 0] }}
                                            transition={{ duration: 1.5, repeat: Infinity }}
                                        >
                                            <ArrowRight size={24} className="text-[#5BA4A4]" />
                                        </motion.div>
                                    </div>
                                )}
                            </motion.div>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
};

// ============================================
// UNIQUE FEATURE 5: Competitor Comparison
// ============================================
const ComparisonTable = () => {
    const features = [
        { name: "AI Trading Bot", us: true, binance: false, coinbase: false, kraken: false },
        { name: "Personal Private Keys", us: true, binance: false, coinbase: false, kraken: false },
        { name: "Destroy Keys Option", us: true, binance: false, coinbase: false, kraken: false },
        { name: "CySEC Regulated", us: true, binance: false, coinbase: true, kraken: true },
        { name: "100x Leverage", us: true, binance: true, coinbase: false, kraken: true },
        { name: "24/7 AI Support", us: true, binance: false, coinbase: false, kraken: false },
        { name: "Zero Deposit Fees", us: true, binance: true, coinbase: false, kraken: true },
        { name: "<10ms Execution", us: true, binance: true, coinbase: false, kraken: false }
    ];

    return (
        <section className="relative py-16 md:py-24 z-10">
            <div className="max-w-5xl mx-auto px-4 md:px-8">
                <motion.div 
                    className="text-center mb-12"
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                >
                    <span className="text-[#5BA4A4] text-xs font-semibold uppercase tracking-[0.2em]">Why Choose Us</span>
                    <h2 className="text-2xl md:text-4xl lg:text-5xl font-black mt-3 mb-4">
                        See How We{' '}
                        <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#5BA4A4] to-purple-400">Compare</span>
                    </h2>
                </motion.div>

                <motion.div
                    className="overflow-x-auto"
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                >
                    <table className="w-full min-w-[600px]">
                        <thead>
                            <tr className="border-b border-white/10">
                                <th className="py-4 px-4 text-left text-sm text-gray-400">Feature</th>
                                <th className="py-4 px-4 text-center">
                                    <div className="flex flex-col items-center">
                                        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#5BA4A4] to-purple-500 flex items-center justify-center mb-1">
                                            <Crown size={20} className="text-white" />
                                        </div>
                                        <span className="text-sm font-bold text-[#5BA4A4]">Us</span>
                                    </div>
                                </th>
                                <th className="py-4 px-4 text-center text-sm text-gray-400">Binance</th>
                                <th className="py-4 px-4 text-center text-sm text-gray-400">Coinbase</th>
                                <th className="py-4 px-4 text-center text-sm text-gray-400">Kraken</th>
                            </tr>
                        </thead>
                        <tbody>
                            {features.map((feature, i) => (
                                <motion.tr
                                    key={i}
                                    className="border-b border-white/5 hover:bg-white/5 transition-colors"
                                    initial={{ opacity: 0, x: -20 }}
                                    whileInView={{ opacity: 1, x: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ delay: i * 0.05 }}
                                >
                                    <td className="py-4 px-4 text-sm text-white">{feature.name}</td>
                                    <td className="py-4 px-4 text-center">
                                        <motion.div
                                            className="w-8 h-8 rounded-full bg-green-500/20 flex items-center justify-center mx-auto"
                                            whileHover={{ scale: 1.2 }}
                                        >
                                            <Check size={18} className="text-green-400" />
                                        </motion.div>
                                    </td>
                                    <td className="py-4 px-4 text-center">
                                        {feature.binance ? (
                                            <div className="w-8 h-8 rounded-full bg-green-500/10 flex items-center justify-center mx-auto">
                                                <Check size={16} className="text-green-400/60" />
                                            </div>
                                        ) : (
                                            <div className="w-8 h-8 rounded-full bg-red-500/10 flex items-center justify-center mx-auto">
                                                <Minus size={16} className="text-red-400/60" />
                                            </div>
                                        )}
                                    </td>
                                    <td className="py-4 px-4 text-center">
                                        {feature.coinbase ? (
                                            <div className="w-8 h-8 rounded-full bg-green-500/10 flex items-center justify-center mx-auto">
                                                <Check size={16} className="text-green-400/60" />
                                            </div>
                                        ) : (
                                            <div className="w-8 h-8 rounded-full bg-red-500/10 flex items-center justify-center mx-auto">
                                                <Minus size={16} className="text-red-400/60" />
                                            </div>
                                        )}
                                    </td>
                                    <td className="py-4 px-4 text-center">
                                        {feature.kraken ? (
                                            <div className="w-8 h-8 rounded-full bg-green-500/10 flex items-center justify-center mx-auto">
                                                <Check size={16} className="text-green-400/60" />
                                            </div>
                                        ) : (
                                            <div className="w-8 h-8 rounded-full bg-red-500/10 flex items-center justify-center mx-auto">
                                                <Minus size={16} className="text-red-400/60" />
                                            </div>
                                        )}
                                    </td>
                                </motion.tr>
                            ))}
                        </tbody>
                    </table>
                </motion.div>
            </div>
        </section>
    );
};

// ============================================
// UNIQUE FEATURE 6: Deposit & Withdrawal Showcase
// ============================================
const DepositWithdrawalShowcase = () => {
    const [activeTab, setActiveTab] = useState('deposit');
    const [keyGenStep, setKeyGenStep] = useState(0);
    const [isDestroying, setIsDestroying] = useState(false);
    
    // Simulate key generation animation
    useEffect(() => {
        if (activeTab === 'deposit') {
            const interval = setInterval(() => {
                setKeyGenStep(prev => (prev + 1) % 4);
            }, 2000);
            return () => clearInterval(interval);
        }
    }, [activeTab]);

    const handleDestroyKey = () => {
        setIsDestroying(true);
        setTimeout(() => setIsDestroying(false), 2000);
    };

    const depositFeatures = [
        { icon: Key, title: "Fresh Wallet Generated", desc: "Unique address created just for you" },
        { icon: Lock, title: "Your Private Keys", desc: "Full ownership - we never store them" },
        { icon: Shield, title: "Destroyable Keys", desc: "Burn your keys anytime for max security" },
        { icon: Zap, title: "Instant Activation", desc: "Start trading within seconds" }
    ];

    const withdrawFeatures = [
        { icon: Clock, title: "Lightning Fast", desc: "Withdrawals processed in under 10 minutes" },
        { icon: Globe, title: "Any Wallet", desc: "Send to any external wallet address" },
        { icon: Shield, title: "Multi-Sig Security", desc: "Protected by multiple signatures" },
        { icon: CheckCircle, title: "No Limits", desc: "Withdraw any amount, anytime" }
    ];

    return (
        <section className="relative py-16 md:py-24 z-10 overflow-hidden">
            {/* Background Effects */}
            <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#5BA4A4]/5 to-transparent" />
            
            <div className="max-w-7xl mx-auto px-4 md:px-8">
                <motion.div 
                    className="text-center mb-12 md:mb-16"
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                >
                    <span className="text-[#5BA4A4] text-xs font-semibold uppercase tracking-[0.2em]">Seamless Transactions</span>
                    <h2 className="text-2xl md:text-4xl lg:text-5xl font-black mt-3 mb-4">
                        Deposit & Withdraw{' '}
                        <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#5BA4A4] to-purple-400">With Confidence</span>
                    </h2>
                    <p className="text-gray-400 max-w-2xl mx-auto text-sm md:text-base">
                        Your funds, your keys, your control. We generate fresh wallets with private keys that only you own.
                    </p>
                </motion.div>

                {/* Tab Switcher */}
                <div className="flex justify-center mb-10">
                    <div className="inline-flex p-1 bg-black/40 backdrop-blur-sm border border-[#5BA4A4]/20 rounded-xl">
                        {['deposit', 'withdraw'].map((tab) => (
                            <motion.button
                                key={tab}
                                onClick={() => setActiveTab(tab)}
                                className={`relative px-6 md:px-8 py-2.5 md:py-3 rounded-lg text-sm font-semibold transition-all ${
                                    activeTab === tab ? 'text-black' : 'text-gray-400 hover:text-white'
                                }`}
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                            >
                                {activeTab === tab && (
                                    <motion.div
                                        layoutId="activeTab"
                                        className="absolute inset-0 bg-gradient-to-r from-[#5BA4A4] to-purple-500 rounded-lg"
                                        transition={{ type: "spring", bounce: 0.2, duration: 0.6 }}
                                    />
                                )}
                                <span className="relative capitalize flex items-center gap-2">
                                    {tab === 'deposit' ? <ArrowDown size={16} /> : <ArrowUpRight size={16} />}
                                    {tab}
                                </span>
                            </motion.button>
                        ))}
                    </div>
                </div>

                <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
                    {/* Left Side - Visual Demo */}
                    <motion.div
                        key={activeTab}
                        initial={{ opacity: 0, x: -30 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.5 }}
                        className="relative"
                    >
                        <div className="relative p-6 md:p-8 bg-gradient-to-br from-[#0a0a0a] via-[#0f0f0f] to-[#050505] border border-[#5BA4A4]/20 rounded-2xl md:rounded-3xl overflow-hidden">
                            {/* Animated border */}
                            <motion.div 
                                className="absolute inset-0 rounded-2xl md:rounded-3xl"
                                style={{ 
                                    background: 'linear-gradient(90deg, transparent, #5BA4A4, transparent)',
                                    backgroundSize: '200% 100%'
                                }}
                                animate={{ backgroundPosition: ['200% 0', '-200% 0'] }}
                                transition={{ duration: 3, repeat: Infinity, ease: 'linear' }}
                            />
                            
                            <div className="relative">
                                {activeTab === 'deposit' ? (
                                    /* Deposit Visual - Wallet Generation */
                                    <div className="space-y-6">
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-3">
                                                <motion.div
                                                    className="p-3 bg-gradient-to-br from-[#5BA4A4]/20 to-purple-500/20 rounded-xl border border-[#5BA4A4]/30"
                                                    animate={{ rotate: [0, 360] }}
                                                    transition={{ duration: 8, repeat: Infinity, ease: 'linear' }}
                                                >
                                                    <Key size={24} className="text-[#5BA4A4]" />
                                                </motion.div>
                                                <div>
                                                    <p className="text-sm text-gray-400">Wallet Generation</p>
                                                    <p className="text-lg font-bold text-white">Fresh Keys Created</p>
                                                </div>
                                            </div>
                                            <motion.div
                                                className="px-3 py-1.5 bg-green-500/20 border border-green-500/30 rounded-full"
                                                animate={{ scale: [1, 1.05, 1] }}
                                                transition={{ duration: 2, repeat: Infinity }}
                                            >
                                                <span className="text-xs text-green-400 font-semibold">SECURE</span>
                                            </motion.div>
                                        </div>

                                        {/* Key Generation Steps */}
                                        <div className="space-y-3">
                                            {['Generating entropy...', 'Creating key pair...', 'Deriving address...', 'Ready to receive!'].map((step, i) => (
                                                <motion.div
                                                    key={i}
                                                    className={`flex items-center gap-3 p-3 rounded-lg transition-all ${
                                                        i <= keyGenStep 
                                                            ? 'bg-[#5BA4A4]/10 border border-[#5BA4A4]/30' 
                                                            : 'bg-black/20 border border-white/5'
                                                    }`}
                                                    initial={{ opacity: 0, x: -20 }}
                                                    animate={{ opacity: 1, x: 0 }}
                                                    transition={{ delay: i * 0.1 }}
                                                >
                                                    <motion.div
                                                        className={`w-6 h-6 rounded-full flex items-center justify-center ${
                                                            i <= keyGenStep ? 'bg-[#5BA4A4]' : 'bg-gray-700'
                                                        }`}
                                                        animate={i === keyGenStep ? { scale: [1, 1.2, 1] } : {}}
                                                        transition={{ duration: 0.5, repeat: i === keyGenStep ? Infinity : 0 }}
                                                    >
                                                        {i < keyGenStep ? (
                                                            <Check size={14} className="text-black" />
                                                        ) : i === keyGenStep ? (
                                                            <motion.div 
                                                                className="w-2 h-2 bg-black rounded-full"
                                                                animate={{ scale: [1, 0.5, 1] }}
                                                                transition={{ duration: 0.5, repeat: Infinity }}
                                                            />
                                                        ) : (
                                                            <span className="text-[10px] text-gray-400">{i + 1}</span>
                                                        )}
                                                    </motion.div>
                                                    <span className={`text-sm ${i <= keyGenStep ? 'text-white' : 'text-gray-500'}`}>
                                                        {step}
                                                    </span>
                                                </motion.div>
                                            ))}
                                        </div>

                                        {/* Destroy Key Button */}
                                        <div className="pt-4 border-t border-white/10">
                                            <p className="text-xs text-gray-500 mb-3">🔥 Unique Feature: Destroy your private key anytime</p>
                                            <motion.button
                                                onClick={handleDestroyKey}
                                                className={`w-full py-3 rounded-xl font-semibold text-sm transition-all ${
                                                    isDestroying 
                                                        ? 'bg-red-500/20 border-2 border-red-500 text-red-400' 
                                                        : 'bg-red-500/10 border border-red-500/30 text-red-400 hover:bg-red-500/20'
                                                }`}
                                                whileHover={{ scale: 1.02 }}
                                                whileTap={{ scale: 0.98 }}
                                            >
                                                {isDestroying ? (
                                                    <motion.span
                                                        initial={{ opacity: 0 }}
                                                        animate={{ opacity: 1 }}
                                                        className="flex items-center justify-center gap-2"
                                                    >
                                                        <motion.div
                                                            animate={{ rotate: 360 }}
                                                            transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                                                        >
                                                            <RefreshCw size={16} />
                                                        </motion.div>
                                                        Destroying Key...
                                                    </motion.span>
                                                ) : (
                                                    <span className="flex items-center justify-center gap-2">
                                                        <Key size={16} /> Destroy Private Key
                                                    </span>
                                                )}
                                            </motion.button>
                                        </div>
                                    </div>
                                ) : (
                                    /* Withdraw Visual */
                                    <div className="space-y-6">
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-3">
                                                <motion.div
                                                    className="p-3 bg-gradient-to-br from-purple-500/20 to-[#5BA4A4]/20 rounded-xl border border-purple-500/30"
                                                    animate={{ y: [0, -5, 0] }}
                                                    transition={{ duration: 2, repeat: Infinity }}
                                                >
                                                    <ArrowUpRight size={24} className="text-purple-400" />
                                                </motion.div>
                                                <div>
                                                    <p className="text-sm text-gray-400">Instant Withdrawal</p>
                                                    <p className="text-lg font-bold text-white">Fast & Secure</p>
                                                </div>
                                            </div>
                                            <motion.div
                                                className="px-3 py-1.5 bg-purple-500/20 border border-purple-500/30 rounded-full"
                                                animate={{ scale: [1, 1.05, 1] }}
                                                transition={{ duration: 2, repeat: Infinity }}
                                            >
                                                <span className="text-xs text-purple-400 font-semibold">NO LIMITS</span>
                                            </motion.div>
                                        </div>

                                        {/* Withdrawal Demo */}
                                        <div className="p-4 bg-black/40 rounded-xl border border-white/10">
                                            <p className="text-xs text-gray-500 mb-2">Withdraw to</p>
                                            <div className="flex items-center gap-2 p-3 bg-black/40 rounded-lg border border-white/10">
                                                <Wallet size={16} className="text-[#5BA4A4]" />
                                                <span className="text-sm text-white font-mono truncate">0x1a2B...9f8E</span>
                                                <Copy size={14} className="text-gray-500 ml-auto cursor-pointer hover:text-[#5BA4A4]" />
                                            </div>
                                        </div>

                                        <div className="p-4 bg-black/40 rounded-xl border border-white/10">
                                            <p className="text-xs text-gray-500 mb-2">Amount</p>
                                            <div className="flex items-center justify-between">
                                                <span className="text-2xl font-bold text-white">2.5 ETH</span>
                                                <span className="text-sm text-gray-400">≈ $8,642.50</span>
                                            </div>
                                        </div>

                                        {/* Processing Steps */}
                                        <div className="flex items-center justify-between p-4 bg-gradient-to-r from-green-500/10 to-transparent rounded-xl border border-green-500/20">
                                            <div className="flex items-center gap-3">
                                                <motion.div
                                                    animate={{ scale: [1, 1.2, 1] }}
                                                    transition={{ duration: 1, repeat: Infinity }}
                                                >
                                                    <CheckCircle size={20} className="text-green-400" />
                                                </motion.div>
                                                <span className="text-sm text-green-400">Processing in ~8 minutes</span>
                                            </div>
                                            <span className="text-xs text-gray-500">0 fees</span>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </motion.div>

                    {/* Right Side - Features List */}
                    <motion.div
                        key={`features-${activeTab}`}
                        initial={{ opacity: 0, x: 30 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.5 }}
                        className="space-y-4"
                    >
                        <div className="mb-6">
                            <h3 className="text-xl md:text-2xl font-bold mb-2">
                                {activeTab === 'deposit' ? 'Your Keys, Your Crypto' : 'Withdraw Anytime'}
                            </h3>
                            <p className="text-gray-400 text-sm">
                                {activeTab === 'deposit' 
                                    ? 'Unlike other platforms, we generate fresh wallets with private keys that only YOU control. Destroy them anytime for maximum security.'
                                    : 'No withdrawal limits, no waiting periods. Your funds are always accessible and can be sent to any wallet worldwide.'
                                }
                            </p>
                        </div>

                        {(activeTab === 'deposit' ? depositFeatures : withdrawFeatures).map((feature, i) => (
                            <motion.div
                                key={i}
                                className="group flex items-start gap-4 p-4 bg-gradient-to-r from-[#0a0a0a] to-transparent border border-white/5 rounded-xl hover:border-[#5BA4A4]/30 transition-all cursor-pointer"
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: i * 0.1 }}
                                whileHover={{ x: 5 }}
                            >
                                <div className="p-2.5 bg-[#5BA4A4]/10 rounded-lg border border-[#5BA4A4]/20 group-hover:border-[#5BA4A4]/50 group-hover:bg-[#5BA4A4]/20 transition-all">
                                    <feature.icon size={20} className="text-[#5BA4A4]" />
                                </div>
                                <div>
                                    <p className="font-semibold text-white group-hover:text-[#5BA4A4] transition-colors">{feature.title}</p>
                                    <p className="text-sm text-gray-500">{feature.desc}</p>
                                </div>
                            </motion.div>
                        ))}

                        {/* Unique Selling Point */}
                        {activeTab === 'deposit' && (
                            <motion.div
                                className="mt-6 p-4 bg-gradient-to-r from-red-500/10 via-orange-500/10 to-yellow-500/10 border border-orange-500/20 rounded-xl"
                                initial={{ opacity: 0, scale: 0.95 }}
                                animate={{ opacity: 1, scale: 1 }}
                                transition={{ delay: 0.5 }}
                            >
                                <div className="flex items-start gap-3">
                                    <motion.div
                                        animate={{ rotate: [0, 10, -10, 0] }}
                                        transition={{ duration: 2, repeat: Infinity }}
                                    >
                                        <span className="text-2xl">🔥</span>
                                    </motion.div>
                                    <div>
                                        <p className="font-bold text-orange-400 text-sm">Industry First: Destroyable Keys</p>
                                        <p className="text-xs text-gray-400 mt-1">
                                            Burn your private keys after withdrawing for ultimate peace of mind. 
                                            No other platform offers this level of security control.
                                        </p>
                                    </div>
                                </div>
                            </motion.div>
                        )}
                    </motion.div>
                </div>
            </div>
        </section>
    );
};

// ============================================
// UNIQUE FEATURE 7: Live Activity Feed
// ============================================
const LiveActivityFeed = () => {
    const [activities, setActivities] = useState([]);
    
    const actions = ['deposited', 'withdrew', 'traded', 'earned'];
    const amounts = ['$1,250', '$5,000', '$12,500', '$25,000', '$50,000', '$2,340', '$8,900'];
    const names = ['John D.', 'Sarah M.', 'Alex K.', 'Maria P.', 'David L.', 'Emma W.', 'Lucas T.', 'Sophie R.'];
    const countries = ['🇺🇸', '🇬🇧', '🇩🇪', '🇫🇷', '🇪🇸', '🇮🇹', '🇯🇵', '🇦🇺', '🇨🇦', '🇸🇬'];
    
    useEffect(() => {
        const addActivity = () => {
            const newActivity = {
                id: Date.now(),
                name: names[Math.floor(Math.random() * names.length)],
                action: actions[Math.floor(Math.random() * actions.length)],
                amount: amounts[Math.floor(Math.random() * amounts.length)],
                country: countries[Math.floor(Math.random() * countries.length)],
                time: 'Just now'
            };
            setActivities(prev => [newActivity, ...prev.slice(0, 4)]);
        };
        
        addActivity();
        const interval = setInterval(addActivity, 4000);
        return () => clearInterval(interval);
    }, []);

    return (
        <div className="w-full max-w-sm mx-auto">
            <div className="flex items-center gap-2 mb-4">
                <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
                </span>
                <span className="text-xs text-gray-400">Live Activity</span>
            </div>
            
            <div className="space-y-2">
                <AnimatePresence mode="popLayout">
                    {activities.map((activity) => (
                        <motion.div
                            key={activity.id}
                            layout
                            initial={{ opacity: 0, x: -20, height: 0 }}
                            animate={{ opacity: 1, x: 0, height: 'auto' }}
                            exit={{ opacity: 0, x: 20, height: 0 }}
                            className="flex items-center gap-3 p-3 bg-black/40 backdrop-blur-sm border border-white/5 rounded-xl"
                        >
                            <span className="text-lg">{activity.country}</span>
                            <div className="flex-1 min-w-0">
                                <p className="text-sm text-white truncate">
                                    <span className="font-semibold">{activity.name}</span>
                                    <span className="text-gray-400"> {activity.action} </span>
                                    <span className="text-[#5BA4A4] font-semibold">{activity.amount}</span>
                                </p>
                                <p className="text-[10px] text-gray-500">{activity.time}</p>
                            </div>
                        </motion.div>
                    ))}
                </AnimatePresence>
            </div>
        </div>
    );
};

// ============================================
// Live Trading Signal Component (Enhanced)
// ============================================
const LiveTradingSignal = ({ type, coin, price, confidence, amount, timestamp, isNew }) => {
    const isBuy = type === 'BUY';
    const accentColor = isBuy ? '#22c55e' : '#ef4444';
    
    return (
        <motion.div
            initial={{ opacity: 0, x: 100, rotateY: -15 }}
            animate={{ opacity: 1, x: 0, rotateY: 0 }}
            exit={{ opacity: 0, x: -100, rotateY: 15 }}
            transition={{ type: "spring", stiffness: 200, damping: 25 }}
            className="relative group cursor-pointer"
            style={{ perspective: '1000px' }}
        >
            <motion.div
                className="absolute -inset-1 rounded-xl blur-lg opacity-50"
                style={{ background: `linear-gradient(135deg, ${accentColor}40, transparent)` }}
                animate={{ opacity: [0.3, 0.6, 0.3] }}
                transition={{ duration: 2, repeat: Infinity }}
            />
            
            <div className={`relative overflow-hidden rounded-xl backdrop-blur-xl border-2 ${
                isBuy ? 'bg-gradient-to-r from-green-950/80 to-black/80 border-green-500/40' 
                      : 'bg-gradient-to-r from-red-950/80 to-black/80 border-red-500/40'
            }`}>
                <motion.div 
                    className="absolute inset-0 rounded-xl"
                    style={{ 
                        background: `linear-gradient(90deg, transparent, ${accentColor}, transparent)`,
                        backgroundSize: '200% 100%'
                    }}
                    animate={{ backgroundPosition: ['200% 0', '-200% 0'] }}
                    transition={{ duration: 3, repeat: Infinity, ease: 'linear' }}
                />
                
                <div className="relative p-3 md:p-4">
                    <div className="flex items-center gap-3 md:gap-4">
                        <div className="relative flex-shrink-0">
                            <motion.div
                                className={`w-10 h-10 md:w-14 md:h-14 rounded-xl flex items-center justify-center ${
                                    isBuy ? 'bg-gradient-to-br from-green-500 to-green-700' : 'bg-gradient-to-br from-red-500 to-red-700'
                                }`}
                                style={{ boxShadow: `0 0 20px ${accentColor}60` }}
                                animate={{ scale: [1, 1.05, 1] }}
                                transition={{ duration: 2, repeat: Infinity }}
                            >
                                {isBuy ? <TrendingUp className="text-white" size={20} /> : <TrendingDown className="text-white" size={20} />}
                            </motion.div>
                            {isNew && (
                                <motion.span 
                                    className="absolute -top-1 -right-1 w-3 h-3 bg-yellow-400 rounded-full"
                                    animate={{ scale: [1, 1.5, 1] }}
                                    transition={{ duration: 0.5, repeat: 3 }}
                                />
                            )}
                        </div>
                        
                        <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                                <span className={`text-xs md:text-sm font-black uppercase ${isBuy ? 'text-green-400' : 'text-red-400'}`}>
                                    {type}
                                </span>
                                <span className="text-white font-bold text-sm md:text-base">{coin}</span>
                            </div>
                            <div className="flex items-center gap-2 text-xs md:text-sm text-gray-400">
                                <span className="font-mono">{amount}</span>
                                <span>@</span>
                                <span className="font-mono font-bold text-white">${price.toLocaleString()}</span>
                            </div>
                        </div>
                        
                        <div className="text-right flex-shrink-0">
                            <div className="flex items-center gap-1 mb-1">
                                <Target size={12} className="text-[#5BA4A4]" />
                                <span className="text-sm md:text-base font-mono font-bold text-white">{confidence}%</span>
                            </div>
                            <p className="text-[10px] text-gray-500">{timestamp}</p>
                        </div>
                    </div>
                </div>
                
                <motion.div 
                    className="h-1"
                    style={{ backgroundColor: accentColor }}
                    initial={{ width: '100%' }}
                    animate={{ width: '0%' }}
                    transition={{ duration: 8, ease: 'linear' }}
                />
            </div>
        </motion.div>
    );
};

// ============================================
// Live Trading Bot Display (Hero Section)
// ============================================
const LiveTradingBot = () => {
    const [trades, setTrades] = useState([]);
    const [stats, setStats] = useState({ buys: 0, sells: 0, profit: 0, accuracy: 94.7 });
    const [systemStatus, setSystemStatus] = useState({ cpu: 67, mem: 45, signal: 98 });
    const [livePrices, setLivePrices] = useState({});

    const coins = ['BTC', 'ETH', 'SOL', 'BNB', 'XRP', 'ADA', 'AVAX', 'DOT', 'MATIC', 'LINK'];
    const strategies = ['MOMENTUM', 'SWING', 'SCALP', 'ARBITRAGE', 'BREAKOUT'];

    // Fetch live prices for realistic BUY/SELL signal prices
    useEffect(() => {
        const fetchLivePrices = async () => {
            try {
                const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/crypto/top-coins?limit=20`);
                if (!res.ok) return;
                const data = await res.json();
                const map = {};
                (data.coins || []).forEach(c => { map[c.symbol] = c.current_price || 0; });
                setLivePrices(map);
            } catch (e) {
                // silent
            }
        };
        fetchLivePrices();
        const iv = setInterval(fetchLivePrices, 60000);
        return () => clearInterval(iv);
    }, []);

    useEffect(() => {
        const initialTrades = Array.from({ length: 2 }, (_, i) => generateTrade(i));
        setTrades(initialTrades);
        
        const interval = setInterval(() => {
            setTrades(prev => {
                const newTrade = generateTrade(Date.now());
                return [newTrade, ...prev.slice(0, 2)];
            });
            
            setStats(prev => ({
                buys: prev.buys + (Math.random() > 0.5 ? 1 : 0),
                sells: prev.sells + (Math.random() > 0.5 ? 1 : 0),
                profit: prev.profit + (Math.random() * 500 - 100),
                accuracy: 94 + Math.random() * 2
            }));
            
            setSystemStatus({
                cpu: 60 + Math.random() * 25,
                mem: 40 + Math.random() * 20,
                signal: 95 + Math.random() * 5
            });
        }, 5000);
        
        return () => clearInterval(interval);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [livePrices]);
    
    const generateTrade = (id) => {
        const coin = coins[Math.floor(Math.random() * coins.length)];
        const basePrice = livePrices[coin] || 100;
        // Small ±0.3% jitter around live price to keep it lively but realistic
        const price = basePrice * (1 + (Math.random() - 0.5) * 0.006);
        return {
            id,
            type: Math.random() > 0.5 ? 'BUY' : 'SELL',
            coin,
            price: price,
            amount: (Math.random() * 2 + 0.1).toFixed(4),
            confidence: (85 + Math.random() * 14).toFixed(1),
            strategy: strategies[Math.floor(Math.random() * strategies.length)],
            timestamp: new Date().toLocaleTimeString('en-US', { hour12: false }),
            isNew: true
        };
    };
    
    return (
        <div className="relative">
            <div 
                className="relative rounded-2xl md:rounded-3xl overflow-hidden"
                style={{ 
                    perspective: '2000px',
                    transform: 'rotateY(-5deg) rotateX(2deg)',
                    transformStyle: 'preserve-3d'
                }}
            >
                <div className="absolute -inset-[2px] bg-gradient-to-r from-[#5BA4A4] via-purple-500 to-[#5BA4A4] rounded-2xl md:rounded-3xl opacity-50 blur-sm" />
                
                <div className="relative bg-gradient-to-br from-[#0a0a0a] via-[#0f0f0f] to-[#050505] rounded-2xl md:rounded-3xl border border-[#5BA4A4]/30 overflow-hidden">
                    {/* Header */}
                    <div className="relative p-3 md:p-5 border-b border-[#5BA4A4]/20">
                        <div className="flex items-center justify-between gap-3">
                            <div className="flex items-center gap-2 md:gap-3">
                                <motion.div
                                    className="relative p-2 md:p-3 bg-gradient-to-br from-[#5BA4A4]/20 to-purple-500/20 rounded-xl border border-[#5BA4A4]/40"
                                    animate={{ boxShadow: ['0 0 10px #5BA4A4', '0 0 25px #5BA4A4', '0 0 10px #5BA4A4'] }}
                                    transition={{ duration: 2, repeat: Infinity }}
                                >
                                    <Bot size={20} className="text-[#5BA4A4] md:w-6 md:h-6" />
                                </motion.div>
                                <div>
                                    <h3 className="text-sm md:text-lg font-black flex items-center gap-2">
                                        <span className="text-white">CYBER</span>
                                        <span className="text-[#5BA4A4]">AI</span>
                                        <span className="px-1.5 py-0.5 text-[8px] md:text-[10px] bg-[#5BA4A4]/20 text-[#5BA4A4] rounded border border-[#5BA4A4]/30">v3.2</span>
                                    </h3>
                                    <div className="flex items-center gap-1.5">
                                        <motion.span 
                                            className="w-2 h-2 rounded-full bg-green-500"
                                            animate={{ scale: [1, 1.3, 1] }}
                                            transition={{ duration: 1, repeat: Infinity }}
                                        />
                                        <span className="text-[10px] md:text-xs text-green-400">LIVE TRADING</span>
                                    </div>
                                </div>
                            </div>
                            
                            <div className="flex items-center gap-2 md:gap-4">
                                <div className="text-center px-2 md:px-3 py-1 md:py-2 rounded-lg bg-green-500/10 border border-green-500/30">
                                    <p className="text-[8px] md:text-[10px] text-green-400/70">BUYS</p>
                                    <p className="text-base md:text-xl font-mono font-black text-green-400">{Math.floor(stats.buys) + 127}</p>
                                </div>
                                <div className="text-center px-2 md:px-3 py-1 md:py-2 rounded-lg bg-red-500/10 border border-red-500/30">
                                    <p className="text-[8px] md:text-[10px] text-red-400/70">SELLS</p>
                                    <p className="text-base md:text-xl font-mono font-black text-red-400">{Math.floor(stats.sells) + 134}</p>
                                </div>
                                <div className="hidden sm:block text-center px-2 md:px-3 py-1 md:py-2 rounded-lg bg-[#5BA4A4]/10 border border-[#5BA4A4]/30">
                                    <p className="text-[8px] md:text-[10px] text-[#5BA4A4]/70">ACCURACY</p>
                                    <p className="text-base md:text-xl font-mono font-black text-[#5BA4A4]">{stats.accuracy.toFixed(1)}%</p>
                                </div>
                            </div>
                        </div>
                        
                        <div className="flex items-center gap-3 md:gap-4 mt-3 p-2 rounded-lg bg-black/40 border border-white/5">
                            <div className="flex items-center gap-1.5 flex-1">
                                <Cpu size={12} className="text-[#5BA4A4]" />
                                <div className="flex-1 h-1.5 bg-[#5BA4A4]/20 rounded-full overflow-hidden">
                                    <motion.div 
                                        className="h-full bg-gradient-to-r from-[#5BA4A4] to-cyan-400 rounded-full"
                                        animate={{ width: `${systemStatus.cpu}%` }}
                                    />
                                </div>
                                <span className="text-[10px] text-[#5BA4A4] font-mono">{systemStatus.cpu.toFixed(0)}%</span>
                            </div>
                            <div className="flex items-center gap-1.5 flex-1">
                                <Activity size={12} className="text-green-400" />
                                <div className="flex-1 h-1.5 bg-green-500/20 rounded-full overflow-hidden">
                                    <motion.div 
                                        className="h-full bg-gradient-to-r from-green-500 to-emerald-400 rounded-full"
                                        animate={{ width: `${systemStatus.signal}%` }}
                                    />
                                </div>
                                <span className="text-[10px] text-green-400 font-mono">{systemStatus.signal.toFixed(0)}%</span>
                            </div>
                        </div>
                    </div>
                    
                    {/* Trade Signals */}
                    <div className="relative p-3 md:p-5 space-y-2 md:space-y-3 min-h-[180px] md:min-h-[220px]">
                        <AnimatePresence mode="popLayout">
                            {trades.map((trade, index) => (
                                <LiveTradingSignal key={trade.id} {...trade} isNew={index === 0} />
                            ))}
                        </AnimatePresence>
                    </div>
                    
                    {/* Footer */}
                    <div className="relative p-3 md:p-4 border-t border-[#5BA4A4]/20 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <motion.div
                                className="w-2 h-2 rounded-full bg-green-500"
                                animate={{ scale: [1, 1.3, 1], opacity: [1, 0.7, 1] }}
                                transition={{ duration: 1.5, repeat: Infinity }}
                            />
                            <span className="text-[10px] md:text-xs text-gray-400">
                                <span className="text-white font-bold">{261 + Math.floor(stats.buys + stats.sells)}</span> signals today
                            </span>
                        </div>
                        <Link 
                            to="/portal?mode=register"
                            className="flex items-center gap-1 text-[10px] md:text-xs text-[#5BA4A4] hover:text-white transition-colors group"
                        >
                            Start Trading <ArrowRight size={12} className="group-hover:translate-x-1 transition-transform" />
                        </Link>
                    </div>
                </div>
            </div>
        </div>
    );
};

// ============================================
// Enhanced Testimonial Card
// ============================================
const TestimonialCard = ({ name, role, location, image, content, stats, rating, delay, onClick }) => (
    <motion.div
        className="relative group cursor-pointer"
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ delay }}
        onClick={onClick}
        whileHover={{ scale: 1.02, y: -5 }}
    >
        <div className="absolute -inset-2 bg-gradient-to-r from-[#5BA4A4]/20 via-purple-500/20 to-[#5BA4A4]/20 rounded-2xl opacity-0 group-hover:opacity-100 blur-xl transition-opacity" />
        
        <div className="relative h-full p-5 md:p-6 bg-gradient-to-br from-[#0a0a0a] to-[#111] border border-[rgba(91,164,164,0.2)] rounded-2xl group-hover:border-[#5BA4A4]/50 transition-all overflow-hidden">
            <Quote className="absolute top-4 right-4 text-[#5BA4A4]/10 w-12 h-12" />
            
            <div className="flex gap-1 mb-4">
                {[...Array(5)].map((_, i) => (
                    <motion.div key={i} initial={{ opacity: 0, scale: 0 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: delay + i * 0.1 }}>
                        <Star size={16} className={i < rating ? "text-yellow-400 fill-yellow-400" : "text-gray-600"} />
                    </motion.div>
                ))}
            </div>
            
            <p className="text-gray-300 mb-6 leading-relaxed text-sm md:text-base italic">"{content}"</p>
            
            {stats && (
                <div className="flex gap-4 mb-5 p-3 bg-[#5BA4A4]/5 rounded-lg border border-[#5BA4A4]/10">
                    {stats.map((stat, i) => (
                        <div key={i} className="text-center flex-1">
                            <p className="text-lg md:text-xl font-bold text-[#5BA4A4]">{stat.value}</p>
                            <p className="text-[10px] text-gray-500 uppercase">{stat.label}</p>
                        </div>
                    ))}
                </div>
            )}
            
            <div className="flex items-center gap-4">
                <div className="relative">
                    <img src={image} alt={name} className="w-12 h-12 md:w-14 md:h-14 rounded-xl object-cover border-2 border-[#5BA4A4]/30" />
                    <motion.div 
                        className="absolute -bottom-1 -right-1 w-5 h-5 bg-green-500 rounded-full flex items-center justify-center border-2 border-[#0a0a0a]"
                        animate={{ scale: [1, 1.1, 1] }}
                        transition={{ duration: 2, repeat: Infinity }}
                    >
                        <CheckCircle size={10} className="text-white" />
                    </motion.div>
                </div>
                <div className="flex-1 min-w-0">
                    <p className="font-bold text-white text-sm md:text-base">{name}</p>
                    <p className="text-xs md:text-sm text-[#5BA4A4]">{role}</p>
                    <p className="text-[10px] text-gray-500 flex items-center gap-1">
                        <MapPin size={10} /> {location}
                    </p>
                </div>
                <ExternalLink size={16} className="text-gray-600 group-hover:text-[#5BA4A4] transition-colors" />
            </div>
        </div>
    </motion.div>
);

// ============================================
// Animated Counter
// ============================================
const Counter = ({ value, suffix = '', prefix = '', decimals = 0, compact = false }) => {
    const [count, setCount] = useState(0);
    const ref = useRef(null);
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        const observer = new IntersectionObserver(([entry]) => {
            if (entry.isIntersecting) setIsVisible(true);
        }, { threshold: 0.3 });
        if (ref.current) observer.observe(ref.current);
        return () => observer.disconnect();
    }, []);

    useEffect(() => {
        if (!isVisible) return;
        const duration = 2000;
        const steps = 60;
        const increment = value / steps;
        let current = 0;
        const timer = setInterval(() => {
            current += increment;
            if (current >= value) { setCount(value); clearInterval(timer); }
            else { setCount(current); }
        }, duration / steps);
        return () => clearInterval(timer);
    }, [isVisible, value]);

    const formatNumber = (num) => {
        if (decimals > 0) return num.toFixed(decimals);
        if (compact && num >= 1000) {
            if (num >= 1000000) return (num / 1000000).toFixed(1).replace(/\.0$/, '') + 'M';
            return (num / 1000).toFixed(0) + 'K';
        }
        return Math.floor(num).toLocaleString();
    };

    return <span ref={ref} className="whitespace-nowrap">{prefix}{formatNumber(count)}{suffix}</span>;
};

// ============================================
// Feature Card
// ============================================
const FeatureCard = ({ icon: Icon, title, description, gradient, index, onClick }) => (
    <motion.div
        className="relative group cursor-pointer"
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ delay: index * 0.1 }}
        onClick={onClick}
        whileHover={{ scale: 1.03, rotateY: 5 }}
        style={{ perspective: '1000px' }}
    >
        <div className="absolute -inset-1 bg-gradient-to-r from-[#5BA4A4]/30 to-purple-500/30 rounded-xl md:rounded-2xl opacity-0 group-hover:opacity-100 blur-lg transition-opacity" />
        <div className="relative h-full p-4 md:p-6 bg-gradient-to-br from-[#0a0a0a] to-[#111] border border-[rgba(91,164,164,0.15)] rounded-xl md:rounded-2xl group-hover:border-[#5BA4A4]/50 transition-all">
            <div className={`absolute inset-0 bg-gradient-to-br ${gradient} rounded-xl md:rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity`} />
            <div className="relative">
                <motion.div 
                    className="w-12 h-12 md:w-16 md:h-16 bg-gradient-to-br from-[#5BA4A4]/20 to-purple-500/20 border border-[#5BA4A4]/30 rounded-xl md:rounded-2xl flex items-center justify-center mb-4 group-hover:border-[#5BA4A4] group-hover:shadow-lg group-hover:shadow-[#5BA4A4]/30 transition-all"
                    whileHover={{ rotate: [0, -10, 10, 0] }}
                    transition={{ duration: 0.5 }}
                >
                    <Icon size={24} className="text-[#5BA4A4] md:w-8 md:h-8" />
                </motion.div>
                <h3 className="text-base md:text-xl font-bold mb-2 group-hover:text-[#5BA4A4] transition-colors">{title}</h3>
                <p className="text-gray-400 text-xs md:text-sm leading-relaxed">{description}</p>
                <motion.div className="mt-4 flex items-center gap-1 text-[#5BA4A4] text-xs md:text-sm opacity-0 group-hover:opacity-100 transition-opacity">
                    Learn more <ArrowRight size={14} />
                </motion.div>
            </div>
        </div>
    </motion.div>
);

// ============================================
// Glow Orb
// ============================================
const GlowOrb = ({ className, color, size = 400, blur = 100 }) => (
    <div className={`fixed pointer-events-none ${className}`} style={{ width: size, height: size, background: `radial-gradient(circle, ${color}20 0%, transparent 70%)`, filter: `blur(${blur}px)`, zIndex: 0 }} />
);

// ============================================
// MAIN COMPONENT
// ============================================
export default function LandingPage() {
    const navigate = useNavigate();
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);

    useEffect(() => {
        const handleScroll = () => setScrolled(window.scrollY > 50);
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const features = [
        { icon: Bot, title: "AI Trading Bot", description: "24/7 automated trading with machine learning algorithms.", gradient: "from-[#5BA4A4]/10 to-transparent" },
        { icon: Key, title: "Your Private Keys", description: "Fresh wallet with keys you own and can destroy.", gradient: "from-purple-500/10 to-transparent" },
        { icon: Shield, title: "Bank-Grade Security", description: "256-bit encryption, 2FA, and cold storage.", gradient: "from-green-500/10 to-transparent" },
        { icon: Zap, title: "100x Leverage", description: "Maximize profits with advanced risk management.", gradient: "from-orange-500/10 to-transparent" },
        { icon: Globe, title: "Multi-Chain", description: "Trade 50+ assets across ERC-20, TRC-20, BEP-20.", gradient: "from-blue-500/10 to-transparent" },
        { icon: Users, title: "24/7 Support", description: "AI chat and personal account managers.", gradient: "from-pink-500/10 to-transparent" }
    ];

    const stats = [
        { value: 125000, suffix: '+', label: 'Active Traders', icon: Users },
        { value: 5.2, prefix: '$', suffix: 'B', label: 'Trading Volume', icon: BarChart3, decimals: 1 },
        { value: 99.99, suffix: '%', label: 'Uptime', icon: Zap, decimals: 2 },
        { value: 190, suffix: '+', label: 'Countries', icon: Globe }
    ];

    const testimonials = [
        { name: "Michael Chen", role: "Portfolio Manager", location: "Singapore", image: TESTIMONIAL_IMAGES.michael, content: "The AI bot has transformed my trading. 47% increase in returns while spending 80% less time.", rating: 5, stats: [{ value: "+47%", label: "Returns" }, { value: "6mo", label: "Using" }, { value: "$2.4M", label: "Managed" }] },
        { name: "Elena Papadopoulos", role: "Investment Director", location: "Cyprus", image: TESTIMONIAL_IMAGES.elena, content: "Institutional-grade security with professional tools. Changed my mind about crypto platforms.", rating: 5, stats: [{ value: "94.7%", label: "Win Rate" }, { value: "2yr", label: "Member" }, { value: "1,200+", label: "Trades" }] },
        { name: "David Mercer", role: "Full-Time Trader", location: "London", image: TESTIMONIAL_IMAGES.david, content: "After trying dozens of platforms, this is hands down the best. Execution speed is incredible.", rating: 5, stats: [{ value: "+312%", label: "ROI" }, { value: "3yr", label: "Trading" }, { value: "24/7", label: "Support" }] }
    ];

    const mediaFeatures = ['Bloomberg', 'Forbes', 'Reuters', 'CNBC', 'CoinDesk', 'TechCrunch'];

    return (
        <div className="min-h-screen bg-[#050505] text-white overflow-x-hidden relative">
            {/* Particle Background */}
            <ParticleConstellation />
            
            {/* Gradient Overlays */}
            <div className="fixed inset-0 bg-gradient-to-b from-[#050505]/50 via-transparent to-[#050505]/80 pointer-events-none z-[1]" />
            <div className="hidden md:block">
                <GlowOrb className="top-[-200px] left-[-200px]" color="#5BA4A4" size={600} blur={150} />
                <GlowOrb className="top-[30%] right-[-300px]" color="#8B5CF6" size={500} blur={120} />
            </div>

            {/* Navigation */}
            <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${scrolled ? 'bg-[#050505]/95 backdrop-blur-xl border-b border-[rgba(91,164,164,0.2)]' : 'bg-transparent'}`}>
                <div className="max-w-7xl mx-auto px-4 md:px-8">
                    <div className="flex items-center justify-between h-16 md:h-20">
                        <Link to="/" className="flex items-center gap-2 md:gap-3 group">
                            <div className="relative">
                                <div className="absolute -inset-1 bg-[#5BA4A4]/20 rounded-xl blur group-hover:bg-[#5BA4A4]/40 transition-colors" />
                                <img src={LOGO_URL} alt="The Cybernetics" className="relative w-9 h-9 md:w-12 md:h-12 rounded-xl border border-[rgba(91,164,164,0.5)]" />
                            </div>
                            <span className="hidden sm:block font-bold text-base md:text-lg tracking-wider">
                                <span className="text-white">THE </span>
                                <span className="text-[#5BA4A4]">CYBERNETICS</span>
                            </span>
                        </Link>

                        <div className="hidden lg:flex items-center gap-8">
                            {['Features', 'Security', 'About', 'Contact'].map(item => (
                                <a key={item} href={`#${item.toLowerCase()}`} className="text-sm text-gray-400 hover:text-[#5BA4A4] transition-colors relative group cursor-pointer">
                                    {item}
                                    <span className="absolute -bottom-1 left-0 w-0 h-px bg-[#5BA4A4] group-hover:w-full transition-all" />
                                </a>
                            ))}
                        </div>

                        <div className="flex items-center gap-2 md:gap-3">
                            <Link to="/portal?mode=login" className="px-3 md:px-4 py-2 text-sm text-gray-300 hover:text-white transition-colors hidden sm:block">Sign In</Link>
                            <Link to="/portal?mode=register" className="relative group px-4 md:px-6 py-2 md:py-2.5 overflow-hidden rounded-xl">
                                <div className="absolute inset-0 bg-gradient-to-r from-[#5BA4A4] to-purple-500 group-hover:from-[#6BB5B5] group-hover:to-purple-400 transition-all" />
                                <span className="relative text-black text-sm font-bold">Get Started</span>
                            </Link>
                            <button className="lg:hidden p-2 text-gray-400 hover:text-white" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
                                {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
                            </button>
                        </div>
                    </div>
                </div>
                
                {/* Mobile Menu */}
                <AnimatePresence>
                    {mobileMenuOpen && (
                        <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className="lg:hidden bg-[#050505]/98 backdrop-blur-xl border-t border-[rgba(91,164,164,0.2)]">
                            <div className="px-4 py-6 space-y-4">
                                {['Features', 'Security', 'About', 'Contact'].map(item => (
                                    <a key={item} href={`#${item.toLowerCase()}`} className="block text-gray-300 hover:text-[#5BA4A4]" onClick={() => setMobileMenuOpen(false)}>{item}</a>
                                ))}
                                <Link to="/portal?mode=login" className="block text-gray-300 hover:text-[#5BA4A4]" onClick={() => setMobileMenuOpen(false)}>Sign In</Link>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </nav>

            {/* Live Price Ticker */}
            <div className="pt-16 md:pt-20 relative z-10">
                <LivePriceTicker />
            </div>

            {/* Hero Section */}
            <section className="relative min-h-[90vh] flex items-center py-12 md:py-20 z-10">
                <div className="max-w-7xl mx-auto px-4 md:px-8 w-full">
                    <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
                        <motion.div initial={{ opacity: 0, x: -50 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.8 }} className="text-center lg:text-left">
                            <motion.div className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#0a0a0a]/80 backdrop-blur-sm border border-[#5BA4A4]/30 rounded-full mb-4 cursor-pointer hover:border-[#5BA4A4] transition-colors" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} onClick={() => navigate('/portal?mode=register')}>
                                <span className="relative flex h-2 w-2">
                                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-500 opacity-75"></span>
                                    <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
                                </span>
                                <span className="text-[10px] md:text-xs text-gray-300 font-medium">LIVE TRADING • 24/7 MARKETS</span>
                                <ArrowRight size={12} className="text-[#5BA4A4]" />
                            </motion.div>

                            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black mb-4 md:mb-6 leading-tight">
                                <span className="block text-white">Trade Smarter</span>
                                <span className="block text-transparent bg-clip-text bg-gradient-to-r from-[#5BA4A4] via-purple-400 to-[#5BA4A4]">With AI Power</span>
                            </h1>

                            <p className="text-sm md:text-lg text-gray-400 mb-6 md:mb-8 max-w-xl mx-auto lg:mx-0 leading-relaxed">
                                Watch our AI execute trades in real-time. Join 125,000+ traders using machine learning to maximize returns 24/7.
                            </p>

                            <div className="flex flex-col sm:flex-row gap-3 md:gap-4 mb-8 justify-center lg:justify-start">
                                <Link to="/portal?mode=register" className="group relative px-6 md:px-8 py-3 md:py-4 overflow-hidden rounded-xl">
                                    <div className="absolute inset-0 bg-gradient-to-r from-[#5BA4A4] to-purple-500 group-hover:scale-105 transition-transform" />
                                    <motion.div className="absolute inset-0 bg-gradient-to-r from-[#5BA4A4] to-purple-500" animate={{ opacity: [0.5, 0.8, 0.5] }} transition={{ duration: 2, repeat: Infinity }} style={{ filter: 'blur(20px)' }} />
                                    <span className="relative flex items-center justify-center gap-2 text-black font-bold text-sm md:text-base">Start Today <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" /></span>
                                </Link>
                                <Link to="/portal?mode=login" className="group px-6 md:px-8 py-3 md:py-4 border border-[rgba(91,164,164,0.3)] text-white font-semibold rounded-xl hover:border-[#5BA4A4] hover:bg-[rgba(91,164,164,0.05)] transition-all flex items-center justify-center gap-2 text-sm md:text-base">
                                    <Play size={16} className="text-[#5BA4A4]" /> Watch Demo
                                </Link>
                            </div>

                            {/* Live Activity Feed */}
                            <div className="mt-6 hidden lg:block">
                                <LiveActivityFeed />
                            </div>
                        </motion.div>

                        <motion.div initial={{ opacity: 0, x: 50 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.8, delay: 0.2 }}>
                            <LiveTradingBot />
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* Global Trading Globe */}
            <section className="relative py-12 z-10">
                <div className="max-w-7xl mx-auto px-4 md:px-8">
                    <motion.div className="text-center mb-8" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
                        <h3 className="text-xl md:text-2xl font-bold">Trading Worldwide in Real-Time</h3>
                        <p className="text-sm text-gray-500 mt-2">Watch live trades happening across the globe</p>
                    </motion.div>
                    <InteractiveGlobe />
                </div>
            </section>

            {/* As Featured In */}
            <section className="relative py-10 md:py-16 z-10">
                <div className="max-w-7xl mx-auto px-4 md:px-8">
                    <motion.div className="relative p-6 md:p-10 rounded-2xl md:rounded-3xl overflow-hidden" initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
                        <div className="absolute inset-0 bg-gradient-to-r from-[#5BA4A4]/10 via-purple-500/5 to-[#5BA4A4]/10" />
                        <div className="absolute inset-0 border border-[#5BA4A4]/20 rounded-2xl md:rounded-3xl" />
                        <motion.div className="absolute inset-0 rounded-2xl md:rounded-3xl" style={{ background: 'linear-gradient(90deg, transparent, rgba(91,164,164,0.3), transparent)', backgroundSize: '200% 100%' }} animate={{ backgroundPosition: ['200% 0', '-200% 0'] }} transition={{ duration: 5, repeat: Infinity, ease: 'linear' }} />
                        
                        <div className="relative">
                            <div className="flex items-center justify-center gap-2 mb-6 md:mb-8">
                                <div className="h-px flex-1 max-w-[100px] bg-gradient-to-r from-transparent to-[#5BA4A4]/50" />
                                <motion.p className="text-xs md:text-sm text-[#5BA4A4] font-bold uppercase tracking-[0.3em] flex items-center gap-2" animate={{ opacity: [0.7, 1, 0.7] }} transition={{ duration: 2, repeat: Infinity }}>
                                    <Star size={14} className="text-yellow-400 fill-yellow-400" /> As Featured In <Star size={14} className="text-yellow-400 fill-yellow-400" />
                                </motion.p>
                                <div className="h-px flex-1 max-w-[100px] bg-gradient-to-l from-transparent to-[#5BA4A4]/50" />
                            </div>
                            
                            <div className="flex flex-wrap justify-center items-center gap-6 md:gap-10 lg:gap-16">
                                {mediaFeatures.map((name, i) => (
                                    <Link key={name} to={`/press/${name.toLowerCase()}`} className="relative group">
                                        <motion.div className="cursor-pointer" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }} whileHover={{ scale: 1.15, y: -5 }}>
                                            <div className="absolute -inset-4 bg-[#5BA4A4]/20 rounded-xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity" />
                                            <div className="relative flex flex-col items-center">
                                                <span className="text-xl md:text-3xl lg:text-4xl font-black text-white/80 group-hover:text-white transition-colors drop-shadow-lg">{name}</span>
                                                <span className="mt-1 text-[9px] md:text-[10px] text-[#5BA4A4] opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1">Read Article <ExternalLink size={10} /></span>
                                            </div>
                                        </motion.div>
                                    </Link>
                                ))}
                            </div>
                            
                            <p className="text-center text-[10px] md:text-xs text-gray-500 mt-6 md:mt-8">Click any publication to read the full article</p>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* Stats Section */}
            <section className="relative py-12 md:py-20 z-10">
                <div className="max-w-7xl mx-auto px-4 md:px-8">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-8">
                        {stats.map((stat, i) => (
                            <motion.div key={i} className="relative group text-center p-4 md:p-6 bg-gradient-to-br from-[#0a0a0a] to-[#111] border border-[rgba(91,164,164,0.1)] rounded-xl md:rounded-2xl cursor-pointer" initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }} whileHover={{ scale: 1.05, borderColor: 'rgba(91,164,164,0.5)' }} onClick={() => navigate('/portal?mode=register')}>
                                <div className="absolute inset-0 bg-gradient-to-b from-[#5BA4A4]/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity rounded-xl md:rounded-2xl" />
                                <stat.icon size={24} className="mx-auto mb-3 text-[#5BA4A4]" />
                                <p className="text-2xl md:text-4xl font-black text-white mb-1 font-mono"><Counter value={stat.value} prefix={stat.prefix} suffix={stat.suffix} decimals={stat.decimals || 0} compact={true} /></p>
                                <p className="text-[10px] md:text-sm text-gray-500">{stat.label}</p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* How It Works */}
            <HowItWorks />

            {/* Features Section */}
            <section id="features" className="relative py-12 md:py-20 z-10">
                <div className="max-w-7xl mx-auto px-4 md:px-8">
                    <motion.div className="text-center mb-10 md:mb-16" initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
                        <span className="text-[#5BA4A4] text-xs font-semibold uppercase tracking-[0.2em]">Features</span>
                        <h2 className="text-2xl md:text-4xl lg:text-5xl font-black mt-3 mb-4">Everything You Need to <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#5BA4A4] to-purple-400">Trade Like a Pro</span></h2>
                    </motion.div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                        {features.map((feature, i) => (<FeatureCard key={i} {...feature} index={i} onClick={() => navigate('/portal?mode=register')} />))}
                    </div>
                </div>
            </section>

            {/* Deposit & Withdrawal Showcase */}
            <DepositWithdrawalShowcase />

            {/* Comparison Table */}
            <ComparisonTable />

            {/* Testimonials */}
            <section className="relative py-12 md:py-20 z-10">
                <div className="max-w-7xl mx-auto px-4 md:px-8">
                    <motion.div className="text-center mb-10 md:mb-16" initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
                        <span className="text-[#5BA4A4] text-xs font-semibold uppercase tracking-[0.2em]">Testimonials</span>
                        <h2 className="text-2xl md:text-4xl lg:text-5xl font-black mt-3 mb-4">Trusted by <span className="text-[#5BA4A4]">Professional Traders</span></h2>
                    </motion.div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
                        {testimonials.map((t, i) => (<TestimonialCard key={i} {...t} delay={i * 0.1} onClick={() => navigate('/portal?mode=register')} />))}
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="relative py-12 md:py-20 z-10">
                <div className="max-w-4xl mx-auto px-4 md:px-8">
                    <motion.div className="relative p-6 md:p-12 overflow-hidden rounded-2xl md:rounded-3xl cursor-pointer" initial={{ opacity: 0, scale: 0.95 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} whileHover={{ scale: 1.02 }} onClick={() => navigate('/portal?mode=register')}>
                        <div className="absolute inset-0 bg-gradient-to-br from-[#5BA4A4]/30 via-[#0a0a0a] to-purple-500/20" />
                        <motion.div className="absolute inset-0 border-2 border-[#5BA4A4]/30 rounded-2xl md:rounded-3xl" animate={{ borderColor: ['rgba(91,164,164,0.3)', 'rgba(91,164,164,0.6)', 'rgba(91,164,164,0.3)'] }} transition={{ duration: 3, repeat: Infinity }} />
                        <div className="relative z-10 text-center">
                            <motion.div animate={{ rotate: [0, 360] }} transition={{ duration: 20, repeat: Infinity, ease: 'linear' }}><Sparkles size={40} className="mx-auto mb-4 text-[#5BA4A4]" /></motion.div>
                            <h2 className="text-2xl md:text-4xl font-black mb-4">Start Trading in 60 Seconds</h2>
                            <p className="text-sm md:text-lg text-gray-400 mb-6 max-w-xl mx-auto">No credit card required. Join 125,000+ traders today.</p>
                            <Link to="/portal?mode=register" className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-[#5BA4A4] to-purple-500 rounded-xl text-black font-bold">Create Free Account <ArrowRight size={18} /></Link>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* Contact */}
            <section id="about" className="relative py-12 md:py-20 z-10">
                <div className="max-w-7xl mx-auto px-4 md:px-8">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12">
                        <div>
                            <h3 className="text-xl md:text-2xl font-bold mb-4">About The Cybernetics</h3>
                            <p className="text-gray-400 mb-4 text-sm md:text-base leading-relaxed">Founded in 2021 in Limassol, Cyprus. Next-generation cryptocurrency trading combining AI with institutional-grade security.</p>
                            <p className="text-gray-400 text-sm md:text-base leading-relaxed">Licensed by CySEC (License No. 271/84). Highest standards of compliance and transparency.</p>
                        </div>
                        <div id="contact">
                            <h3 className="text-xl md:text-2xl font-bold mb-4">Contact Us</h3>
                            <div className="space-y-3">
                                {[{ icon: MapPin, title: "Cyprus Office", content: "Level 12, Limassol Tower, 136 Spyrou Araouzou Street, 3036 Limassol" }, { icon: Phone, title: "Phone", content: "+357 25 030 271" }, { icon: Mail, title: "Email", content: "support@thecybernetics.com" }].map((item, i) => (
                                    <motion.div key={i} className="flex items-start gap-3 p-3 bg-[#0a0a0a]/60 border border-[rgba(91,164,164,0.1)] rounded-lg cursor-pointer hover:border-[#5BA4A4]/50 transition-colors" whileHover={{ x: 5 }}>
                                        <div className="p-2 bg-[#5BA4A4]/10 rounded-lg flex-shrink-0"><item.icon size={16} className="text-[#5BA4A4]" /></div>
                                        <div><p className="font-semibold text-sm">{item.title}</p><p className="text-gray-400 text-xs">{item.content}</p></div>
                                    </motion.div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className="relative py-8 border-t border-[rgba(91,164,164,0.1)] z-10">
                <div className="max-w-7xl mx-auto px-4 md:px-8">
                    <div className="flex flex-col items-center gap-4">
                        <div className="flex items-center gap-3">
                            <img src={LOGO_URL} alt="The Cybernetics" className="w-8 h-8 rounded-lg border border-[rgba(91,164,164,0.3)]" />
                            <span className="font-bold text-[#5BA4A4]">The Cybernetics</span>
                        </div>
                        <div className="flex flex-wrap justify-center gap-4 md:gap-6 text-xs text-gray-500">
                            {['Privacy', 'Terms', 'Risk', 'AML'].map(item => (<a key={item} href="#" className="hover:text-[#5BA4A4] transition-colors cursor-pointer">{item}</a>))}
                        </div>
                        <p className="text-[10px] text-gray-600 text-center">© 2026 The Cybernetics Ltd. CySEC License No. 271/84.</p>
                    </div>
                </div>
            </footer>

            {/* Chat Widget */}
            <LandingChat />
        </div>
    );
}
